import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkflow } from '../../context/WorkflowContext';
import { apiFetch } from '../../api';

const normalizeSubjectString = (value) => {
  return (value || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .join(', ');
};

const splitFullName = (fullName) => {
  const trimmed = (fullName || '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  };
};

/**
 * StudentDetailPanel
 *
 * Slide-in panel showing detailed student information.
 * Includes inline editing for profile fields used by My Students roster.
 */
const StudentDetailPanel = ({
  student,
  tutorInfo,
  onClose,
  onStudentUpdated,
  onStudentDeleted,
  canEdit = false
}) => {
  const navigate = useNavigate();
  const { selectStudent } = useWorkflow();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    full_name: '',
    student_email: '',
    grade_year_level: '',
    target_subjects: '',
    learning_style: '',
    goals: '',
    manual_session_adjustment: 0,
    manual_assigned_at: ''
  });

  const fullName = useMemo(() => {
    const joined = [student?.first_name, student?.last_name].filter(Boolean).join(' ').trim();
    return joined || student?.display_name || '';
  }, [student]);

  useEffect(() => {
    if (!student) return;
    setFormData({
      full_name: fullName,
      student_email: student.owner || '',
      grade_year_level: student.grade_year_level || '',
      target_subjects: student.target_subjects || '',
      learning_style: student.learning_style || '',
      goals: student.goals || '',
      manual_session_adjustment: student.manual_session_adjustment || 0,
      manual_assigned_at: (student.manual_assigned_at || student.assigned_at || '').slice(0, 10)
    });
    setErrors({});
    setIsEditing(false);
  }, [student, fullName]);

  if (!student) return null;

  // Parse subjects
  const subjects = student.target_subjects
    ? student.target_subjects.split(',').map(s => s.trim()).filter(Boolean)
    : [];

  // Get initials for avatar
  const getInitials = () => {
    if (student.first_name || student.last_name) {
      const first = (student.first_name || '').charAt(0);
      const last = (student.last_name || '').charAt(0);
      return `${first}${last}`.toUpperCase() || '?';
    }
    if (student.grade_year_level) {
      const gradeMatch = student.grade_year_level.match(/\d+/);
      return gradeMatch ? gradeMatch[0] : '?';
    }
    return '?';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData.grade_year_level.trim()) {
      nextErrors.grade_year_level = 'Grade level is required.';
    }

    if (!normalizeSubjectString(formData.target_subjects)) {
      nextErrors.target_subjects = 'At least one subject is required.';
    }

    const parsedAdjustment = Number(formData.manual_session_adjustment);
    if (!Number.isInteger(parsedAdjustment)) {
      nextErrors.manual_session_adjustment = 'Session adjustment must be a whole number.';
    }

    const emailVal = formData.student_email.trim();
    const existingOwner = (student.owner || '').trim();
    const DEMO_OWNERS = ['demo-admin@lrbrain.com', 'demo-student'];
    const isExistingDemo = DEMO_OWNERS.includes(existingOwner);
    if (!emailVal) {
      if (!isExistingDemo) {
        nextErrors.student_email = 'Login email is required for non-demo students.';
      }
    } else if (DEMO_OWNERS.includes(emailVal) && !isExistingDemo) {
      nextErrors.student_email = 'Demo login values are only allowed for demo students.';
    } else if (!DEMO_OWNERS.includes(emailVal) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
      nextErrors.student_email = 'Enter a valid email address.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    const { firstName, lastName } = splitFullName(formData.full_name);

    try {
      const response = await apiFetch(`/api/students/${student.uuid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName || null,
          last_name: lastName || null,
          student_email: formData.student_email.trim() || null,
          grade_year_level: formData.grade_year_level.trim(),
          target_subjects: normalizeSubjectString(formData.target_subjects),
          learning_style: formData.learning_style?.trim() || null,
          goals: formData.goals?.trim() || null,
          manual_session_adjustment: parseInt(formData.manual_session_adjustment, 10) || 0,
          manual_assigned_at: formData.manual_assigned_at
            ? `${formData.manual_assigned_at}T00:00:00`
            : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update student');
      }

      const result = await response.json();
      setIsEditing(false);

      if (onStudentUpdated) {
        onStudentUpdated({
          ...student,
          ...result.student,
          owner: result.student?.owner ?? student.owner,
          target_subjects: result.student?.target_subjects || normalizeSubjectString(formData.target_subjects)
        });
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, form: err.message || 'Failed to update student.' }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      full_name: fullName,
      student_email: student.owner || '',
      grade_year_level: student.grade_year_level || '',
      target_subjects: student.target_subjects || '',
      learning_style: student.learning_style || '',
      goals: student.goals || '',
      manual_session_adjustment: student.manual_session_adjustment || 0,
      manual_assigned_at: (student.manual_assigned_at || student.assigned_at || '').slice(0, 10)
    });
    setErrors({});
    setIsEditing(false);
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(
      'Archive this student? This will remove them from active rosters and can be reinstated by admins.'
    );
    if (!confirmed) return;

    setIsSaving(true);
    try {
      const response = await apiFetch(`/api/students/${student.uuid}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to archive student');
      }

      if (onStudentDeleted) {
        onStudentDeleted(student);
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, form: err.message || 'Failed to archive student.' }));
    } finally {
      setIsSaving(false);
    }
  };

  // Navigation handlers
  const handleNavigate = (path) => {
    selectStudent(student);
    navigate(`${path}?student=${student.uuid}`);
    onClose();
  };

  const shortId = student.uuid ? student.uuid.substring(0, 8) : 'Unknown';

  return (
    <div className="student-detail-overlay" onClick={handleOverlayClick}>
      <div className="student-detail-panel">
        {/* Header */}
        <header className="student-detail-panel__header">
          <div className="student-detail-panel__profile">
            {student.profile_image ? (
              <img
                src={student.profile_image}
                alt=""
                className="student-detail-panel__avatar"
              />
            ) : (
              <div className="student-detail-panel__avatar student-detail-panel__avatar--placeholder">
                {getInitials()}
              </div>
            )}
            <div className="student-detail-panel__info">
              <h2 className="student-detail-panel__name">
                {[student.first_name, student.last_name].filter(Boolean).join(' ') || student.display_name || `Student ${shortId}`}
              </h2>
              <span className="student-detail-panel__grade">
                {student.grade_year_level || 'Grade not specified'}
              </span>
            </div>
          </div>
          <div className="student-detail-panel__header-actions">
            {canEdit && !isEditing && (
              <button
                className="student-detail-panel__edit-btn"
                onClick={() => setIsEditing(true)}
                type="button"
              >
                Edit
              </button>
            )}
            <button
              className="student-detail-panel__close"
              onClick={onClose}
              aria-label="Close panel"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="student-detail-panel__content">
          {errors.form && <p className="form-error">{errors.form}</p>}

          {/* Quick Stats */}
          <div className="student-detail-panel__stats">
            <div className="student-detail-panel__stat">
              <span className="student-detail-panel__stat-value">
                {student.session_count || 0}
              </span>
              <span className="student-detail-panel__stat-label">Sessions</span>
            </div>
            <div className="student-detail-panel__stat">
              <span className="student-detail-panel__stat-value">
                {student.confidence_score ? `${student.confidence_score}%` : '-'}
              </span>
              <span className="student-detail-panel__stat-label">Match Score</span>
            </div>
            <div className="student-detail-panel__stat">
              <span className="student-detail-panel__stat-value student-detail-panel__stat-value--small">
                {formatDate(student.manual_assigned_at || student.assigned_at)}
              </span>
              <span className="student-detail-panel__stat-label">Assigned</span>
            </div>
          </div>

          {isEditing ? (
            <div className="student-detail-panel__edit-form">
              <div className="form-group">
                <label htmlFor="student-full-name">Student Name</label>
                <input
                  id="student-full-name"
                  className="form-input"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Student full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="student-email">Student Login Email</label>
                <input
                  id="student-email"
                  type="email"
                  className={`form-input ${errors.student_email ? 'error' : ''}`}
                  name="student_email"
                  value={formData.student_email}
                  onChange={handleChange}
                  placeholder="student@example.com"
                />
                {errors.student_email && <span className="form-error">{errors.student_email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="student-grade">Grade Level <span className="required">*</span></label>
                <input
                  id="student-grade"
                  className={`form-input ${errors.grade_year_level ? 'error' : ''}`}
                  name="grade_year_level"
                  value={formData.grade_year_level}
                  onChange={handleChange}
                  placeholder="e.g., 7th Grade"
                />
                {errors.grade_year_level && <span className="form-error">{errors.grade_year_level}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="student-subjects">Subjects (comma separated) <span className="required">*</span></label>
                <textarea
                  id="student-subjects"
                  className={`form-input ${errors.target_subjects ? 'error' : ''}`}
                  name="target_subjects"
                  value={formData.target_subjects}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Math, Reading, Science"
                />
                {errors.target_subjects && <span className="form-error">{errors.target_subjects}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="student-learning-style">Learning Style</label>
                <input
                  id="student-learning-style"
                  className="form-input"
                  name="learning_style"
                  value={formData.learning_style}
                  onChange={handleChange}
                  placeholder="Visual, Auditory, Kinesthetic..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="student-goals">Goals</label>
                <textarea
                  id="student-goals"
                  className="form-input"
                  name="goals"
                  value={formData.goals}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Key learning goals"
                />
              </div>

              <div className="student-detail-panel__edit-grid">
                <div className="form-group">
                  <label htmlFor="session-adjustment">Session Adjustment (delta)</label>
                  <input
                    id="session-adjustment"
                    type="number"
                    className={`form-input ${errors.manual_session_adjustment ? 'error' : ''}`}
                    name="manual_session_adjustment"
                    value={formData.manual_session_adjustment}
                    onChange={handleChange}
                  />
                  {errors.manual_session_adjustment && <span className="form-error">{errors.manual_session_adjustment}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="assigned-date">Assigned Date Override</label>
                  <input
                    id="assigned-date"
                    type="date"
                    className="form-input"
                    name="manual_assigned_at"
                    value={formData.manual_assigned_at}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="student-detail-panel__edit-actions">
                <button
                  className="btn-cancel"
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn-create"
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Tutor */}
              {(tutorInfo || student.assigned_tutor_name) && (
                <div className="student-detail-panel__section">
                  <h3 className="student-detail-panel__section-title">Assigned Tutor</h3>
                  <p className="student-detail-panel__tutor-name">
                    {tutorInfo?.name || student.assigned_tutor_name}
                  </p>
                </div>
              )}
              {!tutorInfo && !student.assigned_tutor_name && (
                <div className="student-detail-panel__section">
                  <h3 className="student-detail-panel__section-title">Assigned Tutor</h3>
                  <p className="student-detail-panel__unassigned">
                    <span className="roster-table__unassigned-badge">Unassigned</span>
                  </p>
                </div>
              )}

              {/* Subjects */}
              {subjects.length > 0 && (
                <div className="student-detail-panel__section">
                  <h3 className="student-detail-panel__section-title">Subjects</h3>
                  <div className="student-detail-panel__subjects">
                    {subjects.map((subject, idx) => (
                      <span key={idx} className="student-detail-panel__subject-badge">
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Learning Style */}
              {student.learning_style && (
                <div className="student-detail-panel__section">
                  <h3 className="student-detail-panel__section-title">Learning Style</h3>
                  <p className="student-detail-panel__learning-style">
                    {student.learning_style}
                  </p>
                </div>
              )}

              {/* Goals */}
              {student.goals && (
                <div className="student-detail-panel__section">
                  <h3 className="student-detail-panel__section-title">Goals</h3>
                  <p className="student-detail-panel__goals">{student.goals}</p>
                </div>
              )}

              {/* Login Email */}
              <div className="student-detail-panel__section">
                <h3 className="student-detail-panel__section-title">Login Email</h3>
                <p className="student-detail-panel__goals">
                  {student.owner && student.owner !== 'demo-admin@lrbrain.com' && student.owner !== 'demo-student'
                    ? student.owner
                    : <span className="roster-table__unassigned-badge">Not set</span>}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        {!isEditing && (
          <div className="student-detail-panel__actions">
            <button
              className="student-detail-panel__action student-detail-panel__action--secondary"
              onClick={() => handleNavigate('/teacher/sessions')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M2 4H14M2 8H14M2 12H10"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              View Sessions
            </button>
            <button
              className="student-detail-panel__action student-detail-panel__action--primary"
              onClick={() => handleNavigate('/teacher/session-planner')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13 2H3C2.44772 2 2 2.44772 2 3V13C2 13.5523 2.44772 14 3 14H13C13.5523 14 14 13.5523 14 13V3C14 2.44772 13.5523 2 13 2Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M5 6H11M5 9H9"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Plan Session
            </button>
            <button
              className="student-detail-panel__action student-detail-panel__action--accent"
              onClick={() => handleNavigate('/teacher/whiteboard')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="3" width="12" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" />
                <path d="M5 14H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M8 12V14" stroke="currentColor" strokeWidth="1.5" />
              </svg>
              Whiteboard
            </button>
            {canEdit && (
              <button
                className="student-detail-panel__action student-detail-panel__action--danger"
                onClick={handleDelete}
                disabled={isSaving}
              >
                {isSaving ? 'Archiving...' : 'Delete Student'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDetailPanel;
